// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import ErrorsNotification from 'components/others/ErrorNotification';
import {
  forwardGeo,
  getTaxiTypes,
} from '../../../actions/app';
import withEscapeToDismiss from '../../../components/others/WithEscapeToDismiss';
import './SimulationDetailsPanel.scss';
import type { CityDetails, Simulation } from '../../../types/dashboard';
import CommonSimulationParams from './components/CommonSimulationParams';
import NewCohortParams from './components/NewCohortParams';
import CitiesObject from './cities';
import type { Errors } from '../../../types/errors';
import CohortLocationModal from '../../../components/others/CohortLocationModal';

type Props = {
    onStart: Function,
    simulationCompleted: boolean,
    errors: Errors,
    transitionViewport: Function,
    resetErrorState: Function,
    activeCohortID: string,
    back: Function,
    isCohortCreated: boolean,
    forwardGeo: Function,
    autoComplete: Array<any>,
    getTaxiTypes: Function,
    taxiTypes: Array<any>,
};

/* eslint-disable react/prefer-stateless-function */
@withEscapeToDismiss()
export class SimulationDetailsPanel extends Component {
  constructor(props: Props) {
    super(props);
    this.state = {
      cities: new CitiesObject().cities,
      paxVal: 5,
      daxVal: 5,
      city: 'CITY_SG_SIN',
      disableInputs: false,
      timeout: 5,
      currentCohortID: '',
      providerType: 0,
      pickupLatLong: '1.2807142,103.8487429',
      showCohortLocationModal: false,
      taxiTypeDistribution: null,
      isHundredPercent: true,
      showAdvanceSimulationParams: false,
    };
  }

  componentDidMount() {
    this.props.getTaxiTypes('CITY_SG_SIN');
    this.setCurrentCohort(this.props.activeCohortID);
  }

  componentDidUpdate(prevProps: Props) {
    if ((!prevProps.simulationCompleted && this.props.simulationCompleted) ||
        !_.isEmpty(this.props.errors)) {
      this.reenableInputs();
    }

    if (!prevProps.activeCohortID && this.props.activeCohortID) {
      this.setCurrentCohort(this.props.activeCohortID);
    }

    if (prevProps.activeCohortID && !this.props.activeCohortID && !this.props.isCohortCreated) {
      this.setCurrentCohort('');
    }
  }

  setCurrentCohort = (cohortID: string) => {
    this.setState({
      currentCohortID: cohortID,
    });
  };

  setLatLongParam = (latLong: String) => {
    this.setState({
      pickupLatLong: latLong,
    });
  };

  reenableInputs() {
    if (this.state.disableInputs) {
      this.setState({
        disableInputs: false,
      });
    }
  }

  props: Props;

  fillOptions = (type: string) => {
    const arr = [];
    for (let i = 5; i <= 100; i += 5) {
      arr.push(<option key={type + i} value={i}>{i}</option>);
    }
    return arr;
  };

  fillCity = () => {
    const cityArr = [];
    const cities = this.state.cities;
    for (let i = 0; i < cities.length; i += 1) {
      const cityDetails: CityDetails = {
        cityCode: cities[i].city_code,
        latitude: cities[i].latitude,
        longitude: cities[i].longitude,
      };

      cityArr.push(<option
        key={cities[i].name}
        value={JSON.stringify(cityDetails)}
      >{cities[i].name}
      </option>);
    }
    return cityArr;
  };

  fillProviderTypes = () =>
    _.map(['Random', 'Simple', 'Radius'], (val, idx) => <option key={idx} value={idx}>{val}</option>);


  handleNumPaxChange = (event: Event) => {
    this.setState({ paxVal: (event.target: window.HTMLInputElement).value });
  };

  handleNumDaxChange = (event: Event) => {
    this.setState({ daxVal: (event.target: window.HTMLInputElement).value });
  };

  handleCityChange = (event: Event) => {
    const cityDetails: CityDetails = JSON.parse((event.target: window.HTMLInputElement).value);
    this.setState({
      city: cityDetails.cityCode,
      pickupLatLong: cityDetails.latitude.toString().concat(',', cityDetails.longitude.toString()),
    });
    this.props.transitionViewport(cityDetails.latitude, cityDetails.longitude);
    this.props.getTaxiTypes(cityDetails.cityCode);
  };

  handleTimeoutChange = (event: Object) => {
    this.setState({
      timeout: event.target.value,
    });
  };

  handleProviderTypeChange = (event: Object) => {
    this.setState({
      providerType: event.target.value,
    });
  };

  handlePickupLatLongChange = (event: Object) => {
    this.setState({
      pickupLatLong: event.target.value,
    });
  };

  handleShowCohortLocation = (value: boolean) => {
    this.setState({
      showCohortLocationModal: value,
    });
  };

  handleTaxiTypeChange = (taxiTypeRatio: Object, isHundredPercent: boolean) => {
    this.setState({
      taxiTypeDistribution: taxiTypeRatio,
      isHundredPercent,
    });
  };

  toggleAdvanceParams = () => {
    const toggleStatus = this.state.showAdvanceSimulationParams;

    this.setState({
      showAdvanceSimulationParams: !toggleStatus,
      isHundredPercent: toggleStatus,
    });
  };

  render() {
    const disableInput = this.state.disableInputs;
    const autoCompleteLocation = this.props.forwardGeo;
    return (
      <div className="visualization-page ">
        { !_.isEmpty(this.props.errors) &&
          <ErrorsNotification
            errors={this.props.errors}
            dismissible
            onClose={() => {
              this.props.resetErrorState('startSimulation');
            }}
          />
        }
        <div className="section-header map-sidebar-section">
          <h3 className="title is-4">Simulation Settings</h3>
          <div className="columns">
            <h3 className="column is-one-third is-6 cohort-id">Cohort ID: {this.state.currentCohortID}</h3>
            <button
              className="button column back-button is-one-quarter"
              onClick={this.props.back}
              disabled={disableInput}
            >
              Back
            </button>
          </div>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const pLatLong = this.state.pickupLatLong.split(',');
            const simulation: Simulation = {
              numPassengers: parseInt(this.state.paxVal, 10),
              numDrivers: parseInt(this.state.daxVal, 10),
              cohortID: this.state.currentCohortID,
              city: this.state.city,
              timeout: parseInt(this.state.timeout, 10),
              locationDistributor: {
                type: parseInt(this.state.providerType, 10),
                params: {
                  longitude: parseFloat(pLatLong[1]),
                  latitude: parseFloat(pLatLong[0]),
                },
              },
              taxiTypeDistribution: this.state.taxiTypeDistribution,
            };
            this.setState({
              disableInputs: true,
            });
            this.props.onStart(simulation);
          }}
          className="simulation-form"
        >
          <NewCohortParams
            disableInput={disableInput}
            handleNumPaxChange={this.handleNumPaxChange}
            handleNumDaxChange={this.handleNumDaxChange}
            isCohortCreated={this.props.isCohortCreated}
            fillOptions={this.fillOptions}
          />
          <CommonSimulationParams
            disableInput={disableInput}
            handleCityChange={this.handleCityChange}
            handleTimeoutChange={this.handleTimeoutChange}
            handleProviderTypeChange={this.handleProviderTypeChange}
            handlePickupLatLongChange={this.handlePickupLatLongChange}
            fillCity={this.fillCity}
            fillProviderTypes={this.fillProviderTypes}
            showCohortLocationModal={() => { this.handleShowCohortLocation(true); }}
            latlongParams={this.state.pickupLatLong}
            providerType={this.state.providerType}
            taxiTypes={this.props.taxiTypes}
            handleTaxiTypeChange={this.handleTaxiTypeChange}
            toggleAdvanceParams={this.toggleAdvanceParams}
            showAdvanceSimulationParams={this.state.showAdvanceSimulationParams}
          />
          <button
            disabled={!this.state.isHundredPercent || disableInput}
            className="button startSimulationBtn"
            type="submit"
          >Start Simulation</button>
        </form>
        {this.state.showCohortLocationModal && <CohortLocationModal
          showModal={this.state.showCohortLocationModal}
          cohortID={this.state.currentCohortID}
          cohortSetLocation={(locationLatLong) => {
            this.setLatLongParam(locationLatLong);
          }}
          onAutoCompleteLocation={(locationQuery: String) => {
            const countryID = this.state.city.split('_');
            autoCompleteLocation(locationQuery, countryID[1]);
          }}
          handleClose={() => {
            this.handleShowCohortLocation(false);
          }}
          autoCompleteLocations={this.props.autoComplete}
        />}

      </div>
    );
  }
}

function MapStateToProps(state) {
  return {
    autoComplete: state.app.autoComplete,
    taxiTypes: state.app.taxiTypes || [],
  };
}

export default connect(
  MapStateToProps,
  {
    forwardGeo,
    getTaxiTypes,
  }
)(SimulationDetailsPanel);



/** WEBPACK FOOTER **
 ** ./src/js/views/autosim/simulation/SimulationDetailsPanel.jsx
 **/